import { useQuery } from "@tanstack/react-query"
import { ClientOnly, getRouteApi, Link } from "@tanstack/react-router"

import { useUser } from "@repo/auth/hooks/use-user"
import { ThemeToggle } from "@repo/ui/components/theming/theme-toggle"
import { Button, buttonVariants } from "@repo/ui/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@repo/ui/components/ui/dropdown-menu"
import { LogOutIcon, SettingsIcon } from "@repo/ui/components/ui/icons"
import { If } from "@repo/ui/components/ui/if"
import { Skeleton } from "@repo/ui/components/ui/skeleton"
import { UserAvatar } from "@repo/ui/components/ui/user-avatar"
import { cn } from "@repo/ui/lib/utils"

import { getPersonalAccountQueryOptions } from "../queries"

export type AccountHeaderButtonProps = {
  onSignOut?: () => void
  /** Opens the account settings surface (profile + security). */
  onOpenAccountSettings?: () => void
  className?: string
  avatarClassName?: string
  menuSide?: "top" | "right" | "bottom" | "left"
  menuAlign?: "start" | "center" | "end"
}

export function AccountHeaderButton({
  onSignOut,
  onOpenAccountSettings,
  className,
  avatarClassName,
  menuSide = "bottom",
  menuAlign = "end",
}: AccountHeaderButtonProps) {
  const routeContext = getRouteApi("__root__").useRouteContext()
  const { data: user } = useUser({ initialData: routeContext.user })
  const { data: personalAccount, isLoading: isLoadingPersonalAccount } =
    useQuery(getPersonalAccountQueryOptions(user?.id ?? ""))

  if (!user && !isLoadingPersonalAccount) {
    return (
      <Link
        to="/auth/sign-in"
        viewTransition
        className={buttonVariants({ variant: "default", size: "sm" })}
      >
        Sign In
      </Link>
    )
  }

  const displayName = personalAccount?.name
  const email = personalAccount?.email
  const pictureUrl = personalAccount?.avatarUrl ?? ""

  return (
    <ClientOnly>
      <If
        condition={!isLoadingPersonalAccount}
        else={<Skeleton className="size-10 rounded-full" />}
      >
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                variant="ghost"
                size="icon"
                data-testid="account-menu-trigger"
                // !important: the size variant's rounded-input survives the cn
                // merge (custom radius vs built-in), so CSS order would win.
                className={cn("size-10 !rounded-full", className)}
              >
                <UserAvatar
                  id={personalAccount?.id ?? ""}
                  email={email ?? ""}
                  displayName={displayName ?? ""}
                  pictureUrl={pictureUrl || undefined}
                  className={cn("border-border border", avatarClassName)}
                />
              </Button>
            }
          />
          <DropdownMenuContent
            className="min-w-56 rounded-lg"
            side={menuSide}
            align={menuAlign}
            sideOffset={4}
          >
            <DropdownMenuGroup>
              {/* An identity block, not a label — DropdownMenuLabel's mono
                  uppercase treatment is for section headings only. */}
              <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                <UserAvatar
                  id={personalAccount?.id ?? ""}
                  email={email ?? ""}
                  displayName={displayName ?? ""}
                  pictureUrl={pictureUrl || undefined}
                  className="size-8 rounded-lg"
                />
                <div className="grid flex-1 text-left text-sm leading-tight">
                  <span className="truncate font-semibold">{displayName}</span>
                  <span className="text-muted-foreground truncate text-xs">
                    {email}
                  </span>
                </div>
              </div>
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              <DropdownMenuItem onClick={onOpenAccountSettings}>
                <SettingsIcon />
                Account settings
              </DropdownMenuItem>
              <ThemeToggle showLabel asMenuItem />
            </DropdownMenuGroup>

            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onSignOut}>
              <LogOutIcon />
              Sign Out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </If>
    </ClientOnly>
  )
}
